import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, Button, InputNumber, Spin, Typography, Breadcrumb, Tag, Space, App } from 'antd';
import { ShoppingCartOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useCart } from '@digiwedge/ui-axiz';
import { useProductDetails } from '../hooks/useProductDetails';

const { Title, Text, Paragraph } = Typography;

export function ProductDetailPage() {
  // Route params: /product/:brand/:productId/:market
  const { brand, productId, market } = useParams<{ brand: string; productId: string; market: string }>();
  const [quantity, setQuantity] = useState(1);
  const { addItem } = useCart();
  const { message } = App.useApp();

  const { data: product, isLoading } = useProductDetails(
    brand ? parseInt(brand, 10) : undefined,
    productId,
    market ? parseInt(market, 10) : undefined,
  );

  const handleAddToCart = () => {
    if (!product) return;
    addItem({ product, quantity });
    message.success(`Added ${quantity} x ${product.name} to cart`);
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!product) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Title level={4}>Product not found</Title>
        <Link to="/">
          <Button icon={<ArrowLeftOutlined />}>Back to Shop</Button>
        </Link>
      </div>
    );
  }

  const inStock = (product.availableToSell ?? 0) > 0;

  return (
    <div className="product-detail-page">
      <Breadcrumb
        style={{ marginBottom: 24 }}
        items={[
          { title: <Link to="/">Shop</Link> },
          { title: product.name },
        ]}
      />

      <Card>
        <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
          <div style={{ flex: '0 0 300px', maxWidth: '100%' }}>
            {product.imageUrl ? (
              <img
                src={product.imageUrl}
                alt={product.name}
                style={{ width: '100%', borderRadius: 8 }}
              />
            ) : (
              <div
                style={{
                  width: '100%',
                  height: 300,
                  background: '#f0f0f0',
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                No Image
              </div>
            )}
          </div>

          <div style={{ flex: 1, minWidth: 280 }}>
            <Title level={2}>{product.name}</Title>
            <Space style={{ marginBottom: 16 }}>
              <Tag color={inStock ? 'green' : 'red'}>{inStock ? 'In Stock' : 'Out of Stock'}</Tag>
              {product.category && <Tag>{product.category}</Tag>}
            </Space>

            <Title level={3} style={{ color: '#1890ff' }}>
              R {product.price?.toFixed(2)}
            </Title>

            {product.description && (
              <Paragraph style={{ marginBottom: 24 }}>{product.description}</Paragraph>
            )}

            <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 24 }}>
              <Text>Quantity:</Text>
              <InputNumber
                min={1}
                max={product.availableToSell ?? 99}
                value={quantity}
                onChange={(val) => setQuantity(val ?? 1)}
                disabled={!inStock}
              />
            </div>

            <Space>
              <Button
                type="primary"
                size="large"
                icon={<ShoppingCartOutlined />}
                onClick={handleAddToCart}
                disabled={!inStock}
              >
                Add to Cart
              </Button>
              <Link to="/">
                <Button size="large" icon={<ArrowLeftOutlined />}>
                  Continue Shopping
                </Button>
              </Link>
            </Space>
          </div>
        </div>
      </Card>
    </div>
  );
}

export default ProductDetailPage;
